import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle2, Sparkles, UserRound, Zap } from "lucide-react";
import AppHeader from "../../layout/AppHeader";
import { SidebarProvider } from "../../context/SidebarContext";
import ProtectedRoute from "../../components/auth/ProtectedRoute";
import { useAuth } from "../../context/AuthContext";
// import Loader from "../../components/common/Loader";

// const SYNC_STAGES = ["Catalog", "Orders", "Ads", "Analytics"] as const;

function SyncHero({
  isSynced,
  progressPercent,
}: {
  isSynced: boolean;
  progressPercent?: number;
}) {
  return (
    <div className="relative mx-auto w-full max-w-xl px-4">
      <motion.div
        className="pointer-events-none absolute -inset-24 rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(45,157,151,0.4),transparent_68%)] blur-3xl dark:bg-[radial-gradient(ellipse_at_center,rgba(45,157,151,0.28),transparent_72%)]"
        animate={{ opacity: [0.45, 0.95, 0.45], scale: [1, 1.08, 1] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute -right-16 top-4 h-48 w-48 rounded-full bg-indigo-500/25 blur-3xl dark:bg-indigo-400/12"
        animate={{ x: [0, 16, 0], y: [0, -12, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute -left-12 bottom-8 h-44 w-44 rounded-full bg-[color-mix(in_srgb,var(--ok)_30%,transparent)] blur-3xl dark:bg-[color-mix(in_srgb,var(--ok)_12%,transparent)]"
        animate={{ x: [0, -14, 0], y: [0, 14, 0] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-xl border border-gray-200/70 bg-white/75 px-6 py-1 shadow-[0_32px_100px_-20px_rgba(15,23,42,0.18),inset_0_1px_0_0_rgba(255,255,255,0.6)] backdrop-blur-2xl dark:border-white/[0.1] dark:bg-[#0c1524]/90 dark:shadow-[0_32px_100px_-20px_rgba(0,0,0,0.55),inset_0_1px_0_0_rgba(255,255,255,0.04)]"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.45] dark:opacity-[0.2]"
          style={{
            backgroundImage: `linear-gradient(rgba(45,157,151,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(45,157,151,0.08) 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />
        <motion.div
          className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-gradient-to-br from-brand-400/25 to-transparent"
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        />

        <div className="relative min-h-[320px]">
          <motion.div
            className="flex min-h-[320px] flex-col items-center justify-center"
            animate={{ y: isSynced ? 0 : -34 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className={`${isSynced ? "mb-4" : "mb-4"} flex justify-center`}>
              <motion.div
                className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-400 via-brand-500 to-[var(--accent)] text-white shadow-[0_12px_40px_-8px_rgba(45,157,151,0.65)] ring-2 ring-white/30 dark:ring-white/10"
                animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.02, 1] }}
                transition={{
                  duration: 4.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {isSynced ? (
                  <CheckCircle2 className="h-8 w-8" aria-hidden />
                ) : (
                  <>
                    {/* Sync icon using SVG path */}
                    <svg
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 32 32"
                      aria-hidden
                      className="h-8 w-8"
                    >
                      <g>
                        <path
                          fill="#FDFFFF"
                          d="M21,26v-2.519c-3.496,2.338-8.279,1.967-11.364-1.117c-2.992-2.992-3.423-7.579-1.314-11.042
      L11,14V6H3l2.441,2.441c-3.636,5.076-3.19,12.195,1.367,16.751C11.589,29.974,19.196,30.24,24.299,26H21z"
                        />
                        <path
                          fill="#C4CCCC"
                          d="M21,18v8h8l-2.441-2.441c3.636-5.076,3.19-12.195-1.367-16.751C20.411,2.026,12.804,1.76,7.701,6
      H11v2.519c3.496-2.338,8.279-1.967,11.364,1.117c2.992,2.992,3.423,7.579,1.314,11.042L21,18z"
                        />
                        <path
                          fill="#A3AFAF"
                          d="M11,6v2.519c0.525-0.351,1.082-0.634,1.656-0.863L11,6z"
                        />
                        <path
                          fill="#E1E5E5"
                          d="M22.34,27.34c0.683-0.382,1.34-0.826,1.958-1.34H21L22.34,27.34z"
                        />
                      </g>
                    </svg>
                  </>
                )}

                {/* <motion.span
                className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-amber-400 text-amber-950 shadow-lg"
                animate={{ scale: [1, 1.12, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Zap className="h-3.5 w-3.5" aria-hidden />
              </motion.span> */}
              </motion.div>
            </div>

            <div className="mb-2 flex flex-wrap items-center justify-center gap-2 text-center">
              <Sparkles className="h-5 w-5 shrink-0 text-brand-500" aria-hidden />
              <AnimatePresence mode="wait">
                <motion.h1
                  key={isSynced ? "synced" : "syncing"}
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.98 }}
                  transition={{ duration: 0.35 }}
                  className="font-nunito text-[23px] font-bold leading-[1.15] tracking-[-0.035em] tracking-tight text-gray-900 dark:text-white sm:text-3xl"
                >
                  {isSynced ? "All Data Synced!" : "Syncing Your Data"}
                </motion.h1>
              </AnimatePresence>
            </div>
            <motion.p
              className="mx-auto max-w-md text-center text-sm leading-relaxed text-gray-600 dark:text-gray-400"
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35 }}
            >
              {isSynced
                ? "Everything is up to date. You are ready to continue."
                : "We're importing your data across all connected platforms. This may take a few moments."}
            </motion.p>
          </motion.div>

          {/* <div className="mb-6 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {SYNC_STAGES.map((label, i) => {
              const isLast = i === SYNC_STAGES.length - 1;
              return (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                    isLast
                      ? "border-amber-500/35 bg-amber-500/10 text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100"
                      : "border-brand-500/20 bg-brand-500/5 text-brand-800 dark:border-brand-400/25 dark:bg-brand-500/10 dark:text-brand-200"
                  }`}
                >
                  {isLast ? (
                    <span
                      className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-amber-600 border-t-transparent dark:border-amber-300"
                      aria-hidden
                    />
                  ) : (
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white">
                      <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden />
                    </span>
                  )}
                  {label}
                  {isLast ? (
                    <span className="ml-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700/90 dark:text-amber-200/90">
                      · live
                    </span>
                  ) : null}
                </motion.div>
              );
            })}
          </div> */}

          <AnimatePresence>
            {!isSynced ? (
              <motion.div
                key="sync-progress"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-x-0 bottom-0"
              >
                <div className="w-full min-h-[96px]">
                  <div className="relative h-3.5 overflow-hidden rounded-full bg-gray-200/95 ring-1 ring-gray-900/5 dark:bg-white/[0.08] dark:ring-white/10">
                    {typeof progressPercent === "number" ? (
                      <motion.div
                        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-cyan-300 via-brand-500 to-[var(--accent)] shadow-[0_0_24px_rgba(45,157,151,0.7)]"
                        animate={{ width: `${progressPercent}%` }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                      />
                    ) : (
                      <motion.div
                        className="absolute inset-y-0 w-[44%] rounded-full bg-gradient-to-r from-cyan-300 via-brand-500 to-[var(--accent)] shadow-[0_0_24px_rgba(45,157,151,0.7)]"
                        initial={{ left: "-44%" }}
                        animate={{ left: ["-44%", "100%"] }}
                        transition={{ duration: 2.1, repeat: Infinity, ease: "linear" }}
                      />
                    )}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/35 to-transparent"
                      animate={{ x: ["-100%", "220%"] }}
                      transition={{
                        duration: 1.25,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                  </div>
                  <div className="flex justify-center">
                    {typeof progressPercent === "number" ? (
                      <span className="mt-2 text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400">
                        {Math.min(99, Math.round(progressPercent))}%
                      </span>
                    ) : (
                      <>{/* <Loader className="w-20 h-20" /> */}</>
                    )}
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

function LoadingPageInner() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-slate-50 via-slate-100 to-slate-200/90 dark:from-[#050a10] dark:via-[#081018] dark:to-[var(--card)]">
      <AppHeader standaloneBar />
      <SyncLoadingContent />
    </div>
  );
}

/**
 * SyncLoadingContent - embeddable version of the loading-page sync UI.
 * Use this inside pages that are already within the app layout (header/sidebar
 * already rendered) so no duplicate AppHeader is shown.
 */
export function SyncLoadingContent() {
  const [isSynced, setIsSynced] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const { currentShop } = useAuth();
  const shopQuery = currentShop
    ? `?shop=${encodeURIComponent(currentShop)}`
    : "";

  // Clear the pending-sync flag once synced
  useEffect(() => {
    if (isSynced) {
      try {
        localStorage.removeItem("netsights_sync_pending");
      } catch {
        // ignore
      }
    }
  }, [isSynced]);

  useEffect(() => {
    const SYNC_DURATION_MS = 15 * 60 * 1000; // 15 minutes
    const startTime = Date.now();

    const timer = window.setTimeout(() => {
      setIsSynced(true);
      setProgressPercent(100);
    }, SYNC_DURATION_MS);

    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(100, (elapsed / SYNC_DURATION_MS) * 100);
      setProgressPercent(pct);
      if (pct >= 100) {
        window.clearInterval(interval);
      }
    }, 1000);

    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
    };
  }, []);

  return (
    <div className="relative min-h-[calc(100vh-80px)] overflow-hidden bg-gradient-to-b from-slate-50 via-slate-100 to-slate-200/90 dark:from-[#050a10] dark:via-[#081018] dark:to-[var(--card)]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-[0.12]"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, rgba(45,157,151,0.12), transparent 45%),
            radial-gradient(circle at 80% 70%, rgba(99,102,241,0.1), transparent 40%)`,
        }}
      />
      <main className="relative flex min-h-[calc(100dvh-150px)] flex-col items-center justify-center px-4 pb-20 pt-8">
        <SyncHero isSynced={isSynced} progressPercent={progressPercent} />

        <div className="mt-4 flex w-full max-w-xl items-center justify-center">
          <AnimatePresence mode="wait">
            {!isSynced ? (
              <motion.div
                key="pre-sync-actions"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="flex w-full flex-row items-center justify-center gap-3"
              >
                <Link
                  to={`/profile${shopQuery}`}
                  className="inline-flex items-center justify-center gap-2 rounded-[6px] transition dark:bg-[var(--card)] px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-[#FFFFFF0F] hover:bg-gray-50 dark:hover:bg-gray-950"
                >
                  <UserRound
                    className="h-5 w-5 text-brand-600 transition group-hover:scale-110 dark:text-brand-400"
                    aria-hidden
                  />
                  Update your profile
                </Link>
                <Link
                  to={`/notification${shopQuery}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[var(--accent)] hover:bg-[var(--indigo2)] disabled:bg-gray-400 disabled:hover:bg-gray-400 text-white disabled:text-gray-300 rounded-[6px] font-medium text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Bell className="h-5 w-5" aria-hidden />
                  Setup notification
                </Link>
              </motion.div>
            ) : (
              <motion.div
                key="post-sync-action"
                initial={{ opacity: 0, y: 14, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="flex w-full items-center justify-center"
              >
                <Link
                  to={`/notification${shopQuery}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[var(--accent)] hover:bg-[var(--indigo2)] disabled:bg-gray-400 disabled:hover:bg-gray-400 text-white disabled:text-gray-300 rounded-[6px] font-medium text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 w-[150px]"
                >
                  Proceed
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

export default function LoadingPage() {
  return (
    <ProtectedRoute>
      <SidebarProvider>
        <LoadingPageInner />
      </SidebarProvider>
    </ProtectedRoute>
  );
}
